import React, { BaseSyntheticEvent, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCol,
  IonGrid,
  IonImg,
  IonInput,
  IonItem,
  IonLabel,
  IonLoading,
  IonRow,
  IonSpinner,
  useIonViewWillLeave,
} from '@ionic/react'

import { setLoginCallbackUrl, setLoginFailed } from '../store/login.actions'
import { GetUserTypeResponse, ILoginLogo, SSOUserType } from '../store/login.types'

import '../shared/loginForm.component.css'
import { useTranslation } from 'react-i18next'
import Version from '../version/Version.component'
import { decodeAccessToken, isOffline, isPlatformNative } from '../../../utilities'
import { useHistory } from 'react-router'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import {
  setAuthorization,
  setEmail,
  setDatabaseUserName,
  setService,
  setUserId,
  setUserName,
  setUserPassword,
  setTempOktaAccessToken,
  setUserTypeData,
} from '../../user/store/user.actions'
import * as serviceActions from '../../serviceSelection/store/serviceSelection.actions'
import { IServiceDetail, IServiceList } from '../../serviceSelection/store/serviceSelection.types'
import { AuthenticationService } from '../services/authentication.service'
import { identifyUserForLogRocket } from '../../zLogRocket/identify'
import Logger from '../../../services/logger.service'
import {
  handleApiError,
  setErrorMessage,
  showToast,
} from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { setServicesList } from '../../serviceSelection/store/serviceSelection.actions'
import { ZollAPIService } from '../../../services/zollApi.service'
import { UserTypeConstants } from '../store/userType.constants'
import { useOfflineStatus } from '../../../utilities/isOnline.util'
import { EndOfSupportService } from '../services/endOfSupport.service'
import { HttpCodes } from '../../../common/httpCodes.constants'
import {
  checkLocalNotificationPermissions,
  clearLocalNotifications,
  promptForLocalNotificationPermission,
} from '../../../utilities/localNotifications.util'
import LocalStorageService from '../../../services/localStorage.service'
import { hashPin } from '../util/login.util'
import { CrewService } from '../../crew/services/crew.service'
import { ICrewListMember } from '../../pcr/models/crewList.V1'
import { OKTA_LOGIN_URL } from '../../../services/api.service'
import { getLoginSelector } from '../selectors'
import { identifyUserForUserPilot } from '../../userPilot/identify'
import { useDarkMode } from '../../../hooks/useDarkMode'

const LoginForm: React.FC = (): JSX.Element | null => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const login = useSelector(getLoginSelector)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [pin, setPin] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const darkMode = useDarkMode()
  const [loginViaText, setLoginViaText] = useState('')
  const isOfflineStatus = useOfflineStatus()
  const [allowLoginForUserType, setAllowLoginForUserType] = useState(true)
  const [loginViaTextColor, setLoginViaTextColor] = useState<string | undefined>(undefined)
  const [isUserTypeLoading, setIsUserTypeLoading] = useState(false)
  const pinErrorMessage = useRef('')
  const federatedLoginForWebWindowRef = useRef<Window | null>(null)
  const federatedLoginForWebCallbackHandlerRef = useRef<(event: MessageEvent) => void>()

  const logo: ILoginLogo = {
    src: darkMode ? '/assets/images/zoll-logo-white-now.png' : '/assets/images/zoll-logo-blue-now.png',
    altText: 'Zoll Login Logo',
  }
  const { src, altText } = logo

  const inputRef = useRef<any>(null)

  useIonViewWillLeave(() => {
    setUsername('')
    setPassword('')
    setLoginViaText('')
    setLoginViaTextColor(undefined)
    setAllowLoginForUserType(true)
  })

  const onUsernameChange = (usernameParam: string): void => {
    setUsername(usernameParam.trim())
  }

  const onPasswordChange = (passwordParam: string): void => {
    setPassword(passwordParam)
  }

  const onPinChange = (pinParam: string): void => {
    setPin(pinParam)
  }

  const allowForLogin = (): boolean =>
    username.length > 0 && (password.length > 0 || pin.length > 0) && allowLoginForUserType

  const handleLogin = (e: BaseSyntheticEvent): void => {
    e.preventDefault()
    setIsLoading(true)

    isOffline() ? handleOfflineLogin() : handleOnlineLogin()
  }

  const verifyPin = async (pin: string, serviceId: string, userId: string): Promise<boolean> => {
    const inputPin = await hashPin(pin, serviceId, userId)
    const crewService = new CrewService(serviceId)
    const serviceUsers = await crewService.getCrewList()

    if (serviceUsers) {
      const entity = serviceUsers.entity as Array<ICrewListMember>
      const userObj = entity.find((user) => Number(user.userId) === Number(userId))

      if (userObj) {
        const { pin: clientDataPin } = userObj

        if (!clientDataPin) {
          pinErrorMessage.current = t('login.loginForm.noPinError')
          return false
        }

        if (clientDataPin.toUpperCase() === inputPin.toUpperCase()) {
          return true
        }
      }
    }

    pinErrorMessage.current = t('login.loginForm.pinAuthError')
    return false
  }

  const handleOfflineLogin = async (): Promise<void> => {
    const usernameLower = username.toLowerCase()
    let isAuthenticated = false
    let authState = AuthenticationService.getOfflineAuthState(usernameLower)

    if (authState !== null) {
      dispatch(setUserName(usernameLower))
      authState = AuthenticationService.setOfflineAuthState(usernameLower, false)

      let accessToken = null

      if (authState !== null) {
        accessToken = authState.accessToken
      }

      if (accessToken) {
        const { claims } = accessToken
        const { sub: databaseUserName, nameId } = claims

        const serviceListObj = LocalStorageService.getItemForUserIdElseDatabaseUserName(
          nameId.toString(),
          databaseUserName,
          'serviceList'
        )

        if (serviceListObj) {
          const serviceList: IServiceList = JSON.parse(serviceListObj)

          if (serviceList?.length) {
            const userId = serviceList[0].userId
            let serviceId = localStorage.getItem(`${userId}:serviceId`)
            let serviceName = localStorage.getItem(`${userId}:serviceName`)

            if (serviceList.length === 1) {
              serviceId = serviceList[0].id.toString()
              serviceName = serviceList[0].name
            }

            const pinVerified = await verifyPin(pin, serviceId || '', userId.toString())
            if (!pinVerified) {
              handleLoginFailure(ZollAPIService.createMockErrorResponse(401, pinErrorMessage.current))
              return
            }

            authState = AuthenticationService.setOfflineAuthState(usernameLower, true)

            const userEmail = LocalStorageService.getItemForUserIdElseDatabaseUserName(
              nameId.toString(),
              databaseUserName,
              'email'
            )

            dispatch(setUserId(userId))
            dispatch(setEmail(userEmail))
            dispatch(setService(serviceId, serviceName))
          }

          setIsLoading(false)
          dispatch(serviceActions.setServicesList(serviceList))

          const userIdForLogRocket = nameId ? nameId.toString() : usernameLower
          identifyUserForLogRocket(userIdForLogRocket, usernameLower)

          Logger.log(
            `Offline Login success for user: ${username} (${userIdForLogRocket}) - dbUsername: ${databaseUserName}`
          )

          dispatch(serviceActions.setIsLoading(false))
          dispatch(setAuthorization(authState))
          dispatch(setDatabaseUserName(databaseUserName))
          isAuthenticated = true

          const eosMessageForVersion = EndOfSupportService.getEosMessageForVersion()
          if (eosMessageForVersion) {
            dispatch(setErrorMessage(eosMessageForVersion))
            dispatch(showToast(true))
          }

          history.push(AppRouterConstants.CREW_ROUTE)
        }
      }
    }

    if (!isAuthenticated) {
      AuthenticationService.setOfflineAuthState(usernameLower, false)
      handleLoginFailure(ZollAPIService.createMockErrorResponse(401, t('login.loginForm.defaultAuthError')))
    }
  }

  const handleOnlineLogin = (tempAccessToken = ''): void => {
    let singleUserName = username
    let loginData = {}

    if (tempAccessToken) {
      const federatedClaims = decodeAccessToken(tempAccessToken)

      singleUserName = federatedClaims?.sub as string
      loginData = { username: singleUserName, accessToken: tempAccessToken }
    } else {
      loginData = { username, password }
    }

    new AuthenticationService()
      .doLogin(loginData)
      .then((response: any) => {
        if (response.status === HttpCodes.SUCCESS_200) {
          // TODO: In the future the auth api will return more data (e.g. userId, services)
          // that we can pass into Log Rocket identification
          dispatch(setUserPassword(password))
          dispatch(setEmail(response.data?.user?.email))

          const userId = response.data?.user?.userId
          const usernameLower = singleUserName.toLowerCase()
          const databaseUserName = response.data?.user?.username

          LocalStorageService.setItemForUserIdAndCleanDatabaseUserName(
            userId,
            databaseUserName,
            'email',
            response.data?.user?.email
          )

          identifyUserForLogRocket(userId, usernameLower)
          identifyUserForUserPilot(userId, usernameLower)
          Logger.log(`Online Login success for user: ${singleUserName} (${userId}) - dbUsername: ${databaseUserName}`)

          dispatch(setUserName(usernameLower))
          dispatch(setDatabaseUserName(databaseUserName))

          if (tempAccessToken) {
            dispatch(setUserTypeData(SSOUserType))
            dispatch(setTempOktaAccessToken(tempAccessToken))
            dispatch(setLoginCallbackUrl(null))
          }

          const transformedUserServices = response.data.services.map((service: IServiceDetail) => {
            return { ...service, id: service.id + '', userId: service.userId + '' }
          })
          dispatch(setServicesList(transformedUserServices))
          setIsLoading(false)

          if (response.data?.endOfSupport?.endOfSupportSet) {
            const eosMessage = response.data.endOfSupport.endOfSupportMessage
            dispatch(setErrorMessage(eosMessage))
            dispatch(showToast(true))

            EndOfSupportService.crudEndOfSupportForVersion(eosMessage)
          } else {
            EndOfSupportService.removeEndOfSupportForVersion()
          }

          history.push(AppRouterConstants.SERVICE_SELECTION_ROUTE)
        } else {
          handleLoginFailure(response)
        }
      })
      .catch((error) => {
        handleLoginFailure(error)
      })
  }

  const handleLoginFailure = (response: any): void => {
    setIsLoading(false)
    dispatch(setLoginFailed(true))
    dispatch(handleApiError(response))
  }

  const handleFederatedLogin = (e: BaseSyntheticEvent): void => {
    e.preventDefault()

    if (isPlatformNative()) {
      handleFederatedLoginForMobile()
    } else {
      handleFederatedLoginForWeb()
    }
  }

  const handleFederatedLoginForMobile = () => {
    const url = username ? `${OKTA_LOGIN_URL}?userName=${username}` : OKTA_LOGIN_URL

    Logger.info(`FederatedLoginForMobile: opening URL: ${url}`)
    window.open(url, '_system')
  }

  const handleFederatedLoginForWeb = () => {
    const currentOrigin = window.location.origin

    const url = `${OKTA_LOGIN_URL}?origin=${encodeURIComponent(currentOrigin)}`
    const finalUrl = username ? `${url}&userName=${username}` : url

    const width = 500
    const height = 600

    const screenTop = window.screenTop ?? window.screenY
    const screenLeft = window.screenLeft ?? window.screenX

    const top = screenTop + 75
    const left = screenLeft + window.outerWidth / 2 - width / 2
    const features = `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`

    Logger.info(`FederatedLoginForWeb: opening URL: ${finalUrl}`)
    federatedLoginForWebWindowRef.current = window.open(finalUrl, '_blank', features)

    if (federatedLoginForWebCallbackHandlerRef.current) {
      window.removeEventListener('message', federatedLoginForWebCallbackHandlerRef.current)
    }

    federatedLoginForWebCallbackHandlerRef.current = handleFederatedLoginForWebCallbackMessage
    window.addEventListener('message', federatedLoginForWebCallbackHandlerRef.current)
  }

  const handleFederatedLoginForWebCallbackMessage = (event: MessageEvent) => {
    if (federatedLoginForWebWindowRef.current && !federatedLoginForWebWindowRef.current.closed) {
      federatedLoginForWebWindowRef.current.close()
    }

    federatedLoginForWebWindowRef.current = null

    if (federatedLoginForWebCallbackHandlerRef.current) {
      window.removeEventListener('message', federatedLoginForWebCallbackHandlerRef.current)
    }

    const currentOrigin = window.location.origin
    const messageOrigin = event.data?.origin || event.origin

    if (messageOrigin !== currentOrigin) {
      Logger.warn(
        `FederatedLoginForWeb: currentOrigin=${currentOrigin} does not match messageOrigin=${messageOrigin} - callback process cannot continue`
      )
      return
    }

    const messageType = event.data?.type
    if (messageType !== 'auth-complete') {
      Logger.warn(`FederatedLoginForWeb: unexpected messageType of [${messageType}] - callback process cannot continue`)
      return
    }

    Logger.info('FederatedLoginForWeb: successful message back from loginWindow - callback process will continue')

    const callbackUrl = new URL(currentOrigin)
    if (event.data?.at) {
      callbackUrl.searchParams.set('at', event.data?.at)
    }
    if (event.data?.un) {
      callbackUrl.searchParams.set('un', event.data?.un)
    }

    dispatch(setLoginCallbackUrl(callbackUrl.toString()))
  }

  const checkForLocalNotificationPermission = async () => {
    const permissionStatus = await checkLocalNotificationPermissions()
    if (permissionStatus.display === 'prompt' || permissionStatus.display === 'prompt-with-rationale') {
      await promptForLocalNotificationPermission()
    }
  }

  useEffect(() => {
    clearLocalNotifications()
    checkForLocalNotificationPermission()
  }, [])

  useEffect(() => {
    return () => {
      if (federatedLoginForWebCallbackHandlerRef.current) {
        window.removeEventListener('message', federatedLoginForWebCallbackHandlerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    _setLoginViaText()

    if (isOfflineStatus) {
      setPassword('')
    } else {
      setPin('')
    }
  }, [isOfflineStatus])

  useEffect(() => {
    const timeoutId = setTimeout(() => _setLoginViaText(), 1000)
    return () => clearTimeout(timeoutId)
  }, [username])

  useEffect(() => {
    setIsLoading(true)

    if (!login.loginCallbackUrl) {
      handleLoginCallbackFailure('')
      return
    }

    // Get the accessToken from the URL
    const urlObj = new URL(login.loginCallbackUrl)
    const accessToken = urlObj.searchParams.get('at')
    if (!accessToken) {
      handleLoginCallbackFailure(
        'No access token was sent back to the app. Please close and re-launch the application to log in.'
      )
      return
    }

    // Get the userName from the URL
    const userNameFromUrl = urlObj.searchParams.get('un')
    if (!userNameFromUrl) {
      handleLoginCallbackFailure(
        'No userName was sent back to the app. Please close and re-launch the application to log in.'
      )
      return
    }

    // Check accessToken
    const federatedClaims = decodeAccessToken(accessToken)
    if (!federatedClaims || !federatedClaims.sub?.trim()) {
      handleLoginCallbackFailure('Invalid access token found. Please close and re-launch the application to log in.')
      return
    }

    // Make sure the sub == the intended user
    if (federatedClaims.sub !== userNameFromUrl.toLowerCase()) {
      handleLoginCallbackFailure(
        'Previous user access token found. Please close and re-launch the application to log in.'
      )
      return
    }

    handleOnlineLogin(accessToken)
  }, [login.loginCallbackUrl])

  const handleLoginCallbackFailure = (errMessage: string) => {
    setIsLoading(false)

    if (errMessage) {
      Logger.error(errMessage)
      setLoginViaText(errMessage)
      setLoginViaTextColor('danger')
    }
  }

  const _setLoginViaText = () => {
    setAllowLoginForUserType(true)
    setLoginViaText('')
    setLoginViaTextColor(undefined)

    if (isOfflineStatus) {
      setLoginViaText(t('login.loginForm.viaPIN'))
      return
    }

    if (!username) {
      setLoginViaText('')
      return
    }

    setIsUserTypeLoading(true)

    new AuthenticationService()
      .doGetUserType({ username })
      .then((response: GetUserTypeResponse) => {
        setIsUserTypeLoading(false)

        if (response.status === HttpCodes.SUCCESS_200) {
          const userType = response.data.userType
          Logger.log(`GetUserType success for user: ${username} - userType: ${userType}`)

          if (UserTypeConstants.USER_TYPE_SSO === userType) {
            setLoginViaText(t('login.loginForm.viaZOLLOnlineSSO'))
          } else if (UserTypeConstants.USER_TYPE_SSO_INACTIVE === userType) {
            setAllowLoginForUserType(false)
            setLoginViaText(t('login.loginForm.ssoInactiveMessage'))
            setLoginViaTextColor('danger')
          } else if (UserTypeConstants.USER_TYPE_ACCOUNT_LOCKED === userType) {
            setAllowLoginForUserType(false)
            setLoginViaText(t('login.loginForm.accountLockedMessage'))
            setLoginViaTextColor('danger')
          } else if (UserTypeConstants.USER_TYPE_ORACLE === userType) {
            setLoginViaText(t('login.loginForm.viaZOLLEmsChartsWeb'))
          } else {
            setLoginViaText(userType)
            setLoginViaTextColor('danger')
          }
        }
      })
      .catch((error) => {
        setIsUserTypeLoading(false)
        Logger.error(error)
      })
  }

  const renderPasswordField = () => {
    return (
      <IonRow>
        <IonCol>
          <IonItem>
            <IonInput
              data-testid='Password'
              placeholder='Password'
              id='password'
              type='password'
              value={password}
              onIonChange={(e) => onPasswordChange(`${e.detail.value}`)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  allowForLogin() && handleLogin(e)
                }
              }}
              ref={(ref) => (inputRef.current = ref)}
            />
          </IonItem>
        </IonCol>
      </IonRow>
    )
  }

  const renderPinField = () => {
    return (
      <IonRow>
        <IonCol>
          <IonItem>
            <IonInput
              data-testid='Pin'
              placeholder='PIN'
              id='pin'
              type='password'
              value={pin}
              onIonChange={(e) => onPinChange(`${e.detail.value}`)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  allowForLogin() && handleLogin(e)
                }
              }}
              ref={(ref) => (inputRef.current = ref)}
            />
          </IonItem>
        </IonCol>
      </IonRow>
    )
  }

  const renderFederatedLogin = () => {
    return (
      <>
        <IonRow>
          <IonCol>
            <div className='federated-divider'>
              <span className='federated-divider-text'>{t('login.loginForm.federatedDividerText')}</span>
            </div>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonButton
            data-testid='federatedSignIn'
            className='btn btn-primary button-clear'
            expand='block'
            onClick={handleFederatedLogin}>
            {t('login.loginForm.federatedSignInText')}
          </IonButton>
        </IonRow>
      </>
    )
  }

  return (
    <>
      <IonLoading isOpen={isLoading} animated showBackdrop spinner={'crescent'} />

      <IonCard qa-id='main_login_card'>
        <IonCardContent>
          <IonGrid>
            <IonRow>
              <IonCol class='logo-row' size='10' offset='1'>
                <IonImg src={src} alt={altText} />
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol>
                <IonItem>
                  <IonInput
                    data-testid='UserName'
                    placeholder='Username'
                    id='username'
                    type='text'
                    value={username}
                    onIonChange={(e) => onUsernameChange(`${e.detail.value}`)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        inputRef.current.setFocus()
                      }
                    }}
                  />
                </IonItem>
              </IonCol>
            </IonRow>
            {!isOfflineStatus && renderPasswordField()}
            {isOfflineStatus && renderPinField()}
            <IonRow>
              <IonCol>
                <IonButton
                  className='btn btn-primary button-clear'
                  data-testid='Login'
                  expand='block'
                  onClick={handleLogin}
                  disabled={!allowForLogin()}>
                  {t('login.loginForm.loginButtonText')}
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow className='login-via-section'>
              <IonCol>
                {isUserTypeLoading && <IonSpinner name='lines-sharp-small' />}
                {!isUserTypeLoading && (
                  <IonLabel color={loginViaTextColor} data-testid='LoginViaText'>
                    {loginViaText}
                  </IonLabel>
                )}
              </IonCol>
            </IonRow>
            {!isOfflineStatus && renderFederatedLogin()}
          </IonGrid>
        </IonCardContent>
      </IonCard>

      <Version />
    </>
  )
}

export default LoginForm
